import { useEffect, useRef } from 'react';
import { useNavigate, useLocation, useNavigationType } from 'react-router-dom';

const ALLOWED_HOSTS = ['openrunsusa.com', 'www.openrunsusa.com'];
// Invitation/share routes only; keep aligned with the public AASA components.
const ALLOWED_PATH_PREFIXES = ['/r/', '/run/', '/club/', '/games/', '/j/', '/join/'];

function internalRoute(raw) {
  if (typeof raw !== 'string' || !raw) return null;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:' || !ALLOWED_HOSTS.includes(url.hostname)
        || url.username || url.password || url.port
        || !ALLOWED_PATH_PREFIXES.some(prefix => url.pathname.startsWith(prefix))) return null;
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function UniversalLinksHandler() {
  const navigate = useNavigate();
  const location = useLocation();
  const navigationType = useNavigationType();
  const currentRoute = `${location.pathname}${location.search}${location.hash}`;
  const routeRef = useRef(currentRoute);
  const startupRef = useRef(null);
  const navigateRef = useRef(navigate);
  useEffect(() => { navigateRef.current = navigate; }, [navigate]);
  useEffect(() => {
    routeRef.current = currentRoute;
    const startup = startupRef.current;
    if (!startup) return;
    if (currentRoute === startup.route) startup.arrived = true;
    // Short-link resolution replaces /j/ with its target; that remains
    // the same startup journey. A subsequent PUSH/POP releases coalescing.
    else if (navigationType !== 'REPLACE' && (startup.arrived || currentRoute !== startup.origin)) startupRef.current = null;
  }, [currentRoute, navigationType]);
  useEffect(() => {
    const app = typeof window !== 'undefined' ? window?.Capacitor?.Plugins?.App : undefined;
    if (!app || typeof app.addListener !== 'function') return undefined;
    let active = true;
    let warmNavigation = false;
    const handleUrl = (event, source) => {
      if (!active) return;
      const route = internalRoute(event?.url);
      if (!route) return;
      if (source === 'launch') {
        // A newer user tap wins over an asynchronously resolved launch URL.
        if (warmNavigation) return;
        // No timing assumption: duplicate startup reports are coalesced until
        // the user leaves the initial destination. Reopening it later still works.
        startupRef.current = { route, origin: routeRef.current, arrived: routeRef.current === route };
      } else {
        warmNavigation = true;
        if (startupRef.current?.route === route) return;
        startupRef.current = null;
      }
      navigateRef.current(route, { replace: false });
    };
    let listener;
    try {
      listener = app.addListener('appUrlOpen', event => handleUrl(event, 'warm'));
      Promise.resolve(listener).catch(() => {
        console.warn('[UniversalLinks] reason=universal_link_listener_failed');
      });
      if (typeof app.getLaunchUrl === 'function') {
        Promise.resolve(app.getLaunchUrl())
          .then(event => handleUrl(event, 'launch'))
          .catch(() => console.warn('[UniversalLinks] reason=universal_link_launch_lookup_failed'));
      }
    } catch {
      console.warn('[UniversalLinks] reason=universal_link_initialization_failed');
    }
    return () => {
      active = false;
      Promise.resolve(listener).then(handle => handle?.remove?.())
        .catch(() => console.warn('[UniversalLinks] reason=universal_link_listener_cleanup_failed'));
    };
  }, []);
  return null;
}

export default UniversalLinksHandler;
